import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {AiTextPromptBox} from '@Components/ai-text-prompt-box/ai-text-prompt-box';
import {Text, TextSize} from '@Components/text';
import {AiButton} from '@Components/ai-button';
import {Size} from '@Components/button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {AiTextPromptBoxError} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-error';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ErrorType, MiniAiTextPromptBoxProps, OneOfPromptOption, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {handleErrorAndGetErrorMessage} from '@Libraries/ai-text-prompt-box-library';
import type {Editor} from 'grapesjs';
import styles from './email-maker-ai-text-prompt-box.module.scss';
import {GENERATION_OPTIONS_SESSION_STORAGE_VAR, PROMPT_SESSION_STORAGE_VAR, TEMPLATE_AI_REGENERATION} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {useAppSelector} from '@/hooks';
import {addLoadingComponent, isLoading, removeLoadingComponent} from '@Components/email-maker-ai-text-prompt-box/email-maker-ai-text-prompt-box.reducer';
import {setErrorMessage} from '@Components/email-maker-ai-text-prompt-box-dropdown/email-maker-ai-text-prompt-box-dropdown-reducer';
import {EmailEditorAiPromptBoxVariant, EmailMakerAiRewriteMode} from '@Libraries/ai-email-campaign-content-library';

export interface EmailMakerAiTextPromptBoxProps extends MiniAiTextPromptBoxProps {
  editor: Editor;
  emailEditorAiPromptBoxVariant: EmailEditorAiPromptBoxVariant;
  addHeader?: boolean;
  headerLabel?: string;
  defaultHeaderButtonText?: string;
  showPromptBoxOnRender?: boolean;
  defaultErrorBoxMessage?: string;
}

export function EmailMakerAiTextPromptBox({
  editor,
  emailEditorAiPromptBoxVariant,
  addHeader = true,
  headerLabel = window.i18next.t('pmwjs_content'),
  defaultHeaderButtonText = window.i18next.t('pmwjs_ai_writer'),
  showPromptBoxOnRender = false,
  defaultErrorBoxMessage = undefined,
  ...props
}: EmailMakerAiTextPromptBoxProps): ReactElement<any> {
  const [showPromptBox, setShowPromptBox] = useState(showPromptBoxOnRender);
  const [aiButtonIcon, setAiButtonIcon] = useState('icon-ai-caption');
  const [aiButtonText, setAiButtonText] = useState(defaultHeaderButtonText);
  const [generateImagesUsingAi, setGenerateImagesUsingAi] = useState(false);
  const [generateDesignsUsingAi, setGenerateDesignsUsingAi] = useState(false);
  const [errorBoxMessage, setErrorBoxMessage] = useState(defaultErrorBoxMessage);

  const editorContainer = document.getElementById('html-editor-container');
  const isImageGenerationEnabled = editorContainer?.getAttribute('data-editor-ai-image-generation-via-prompt-box-enabled') === '1';
  const isDesignUpdatesEnabled = editorContainer?.getAttribute('data-editor-ai-design-updates-via-prompt-box-enabled') === '1';

  const isPromptBoxLoading = useAppSelector((state) => {
    return isLoading(state, emailEditorAiPromptBoxVariant, editor);
  });

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      sessionStorage.removeItem(PROMPT_SESSION_STORAGE_VAR);
      sessionStorage.removeItem(GENERATION_OPTIONS_SESSION_STORAGE_VAR);
    };

    if (sessionStorage.getItem(PROMPT_SESSION_STORAGE_VAR) || sessionStorage.getItem(GENERATION_OPTIONS_SESSION_STORAGE_VAR)) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const showErrorBox = (): boolean => {
    return errorBoxMessage !== undefined;
  };

  const getDefaultPrompt = (): string | undefined => {
    if (sessionStorage.getItem(PROMPT_SESSION_STORAGE_VAR)) {
      return JSON.parse(sessionStorage.getItem(PROMPT_SESSION_STORAGE_VAR) as string) as string;
    }

    return props.defaultPrompt;
  };

  const getDefaultGenerationOptions = (): SelectedValueType | undefined => {
    if (sessionStorage.getItem(GENERATION_OPTIONS_SESSION_STORAGE_VAR)) {
      JSON.parse(sessionStorage.getItem(GENERATION_OPTIONS_SESSION_STORAGE_VAR) as string) as SelectedValueType;
    }

    return props.defaultGenerationOptions;
  };

  const setDefaultPrompt = (prompt: string): void => {
    sessionStorage.setItem(PROMPT_SESSION_STORAGE_VAR, JSON.stringify(prompt));
  };

  const setDefaultGenerationOptions = (updatedGenerationOptions: SelectedValueType): void => {
    sessionStorage.setItem(GENERATION_OPTIONS_SESSION_STORAGE_VAR, JSON.stringify(updatedGenerationOptions));
  };

  const updateDefaultGenerationOption = (changedProperty: OneOfPromptOption): void => {
    const {key, value} = changedProperty;
    const updatedGenerationOptions = {
      ...getDefaultGenerationOptions(),
      [key]: value,
    };

    setDefaultGenerationOptions(updatedGenerationOptions);
  };

  const addToLoadingComponents = (componentId: string): void => {
    window.PMW.redux.store.dispatch(addLoadingComponent(componentId));
  };

  const removeFromLoadingComponents = (componentId: string): void => {
    window.PMW.redux.store.dispatch(removeLoadingComponent(componentId));
  };

  const addTemplateToLoadingComponents = (): void => {
    window.PMW.redux.store.dispatch(addLoadingComponent(TEMPLATE_AI_REGENERATION));
  };

  const removeTemplateFromLoadingComponents = (): void => {
    window.PMW.redux.store.dispatch(removeLoadingComponent(TEMPLATE_AI_REGENERATION));
  };

  // CodeReviewZain: please rename SelectedValueType to be a bit more explanatory. select values of what exactly? also, this is a record
  // Record<string, PromptBoxConfigOptionValue>. Can we change 'string' to be a different type, even if it's very simple?
  // Example, we've made a type for `type posterHashedId = string` and then use it as `Record<posterHashedId, Data>` instead
  // of `Record<string, Data>`. makes understanding it a lot easier.
  // Do the same with ListAndListItems
  const regenerateEmailContent = async (prompt: string, selectedValues: SelectedValueType): Promise<void> => {
    switch (emailEditorAiPromptBoxVariant) {
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR_DROPDOWN:
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR:
        await rewriteSelectedComponentContent(prompt, selectedValues);
        return;
      case EmailEditorAiPromptBoxVariant.TEMPLATE_EDITOR:
        await rewriteContent(prompt, selectedValues);
        await Promise.all([regenerateImages(prompt), regenerateDesigns(prompt, selectedValues)]);
        return;
    }
  };

  const regenerateDesigns = async (prompt: string, selectedValues: SelectedValueType): Promise<void> => {
    if (generateDesignsUsingAi) {
      await editor.runCommand(window.PMW.emails.commands.AI_REGENERATE_EMAIL_TEMPLATE_DESIGNS, {prompt, selectedValues});
    }
  };

  const regenerateImages = async (prompt: string): Promise<void> => {
    if (generateImagesUsingAi) {
      await editor.runCommand(window.PMW.emails.commands.AI_REGENERATE_EMAIL_TEMPLATE_IMAGES, {prompt});
    }
  };

  const rewriteContent = async (prompt: string, selectedValues: SelectedValueType): Promise<void> => {
    try {
      addTemplateToLoadingComponents();
      setShowPromptBox(false);

      editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true});
      await editor.runCommand(window.PMW.emails.commands.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {
        prompt,
        selectedValues,
        rewriteMode: EmailMakerAiRewriteMode.REWRITE_ALL_TEXT_COMPONENTS,
      });

      setAiButtonIcon('icon-check');
      setAiButtonText(window.i18next.t('pmwjs_done'));

      setTimeout(() => {
        setAiButtonIcon('icon-ai-caption');
        setAiButtonText(defaultHeaderButtonText);
      }, 2000);
    } catch (e) {
      const error = e as ErrorType;
      const errorMessageContent = handleErrorAndGetErrorMessage(error);

      if (errorMessageContent) {
        setErrorBoxMessage(errorMessageContent);
      }
    } finally {
      removeTemplateFromLoadingComponents();
      editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
    }
  };

  const showSingleTextEditorError = (errorMessageContent: string): void => {
    setErrorBoxMessage(errorMessageContent);
  };

  const showSingleTextEditorDropdownError = (errorMessageContent: string): void => {
    window.PMW.redux.store.dispatch(setErrorMessage(errorMessageContent));
  };

  const showTextEditorError = (errorMessageContent: string): void => {
    switch (emailEditorAiPromptBoxVariant) {
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR:
        showSingleTextEditorError(errorMessageContent);
        break;
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR_DROPDOWN:
        showSingleTextEditorDropdownError(errorMessageContent);
        break;
    }
  };

  const rewriteSelectedComponentContent = async (prompt: string, selectedValues: SelectedValueType): Promise<void> => {
    const selectedComponent = editor.getSelected();
    if (!selectedComponent) {
      return;
    }

    try {
      if (emailEditorAiPromptBoxVariant === EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR) {
        setShowPromptBox(false);
      }

      addToLoadingComponents(selectedComponent.ccid);
      editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: true, component: selectedComponent});

      await editor.runCommand(window.PMW.emails.commands.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {
        prompt,
        selectedValues,
        rewriteMode: EmailMakerAiRewriteMode.REWRITE_SELECTED_TEXT_COMPONENT,
      });
    } catch (e) {
      const error = e as ErrorType;
      const errorMessageContent = handleErrorAndGetErrorMessage(error);

      if (errorMessageContent) {
        showTextEditorError(errorMessageContent);
      }
    } finally {
      editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: selectedComponent});
      removeFromLoadingComponents(selectedComponent.ccid);
    }
  };

  const toggleShowPromptBox = (): void => {
    setShowPromptBox(!showPromptBox);
    setErrorBoxMessage(undefined);
  };

  const toggleGenerateImagesUsingAi = (): void => {
    setGenerateImagesUsingAi(!generateImagesUsingAi);
  };

  const toggleGenerateDesignsUsingAi = (): void => {
    setGenerateDesignsUsingAi(!generateDesignsUsingAi);
  };

  const getHeaderLabel = (): ReactElement<any> => {
    return (
      <div className={`flex-row-align-center ${styles.labelContainer}`}>
        <Text val={headerLabel} size={TextSize.XSMALL} className="content-body" />
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.TEXT,
            text: window.i18next.t('pmwjs_new'),
            isBold: true,
            textColor: 'content-body-white',
          }}
          background={PILL_BACKGROUND_COLOR.SECONDARY_100}
          size={PILL_SIZE.XSMALL}
          className="spacing-p-l-2 spacing-p-r-2"
        />
      </div>
    );
  };

  const getHeaderButton = (): ReactElement<any> => {
    if (isUserLoggedIn() && isUserPremium()) {
      return (
        <AiButton
          variant={AIButtonVariant.SUBDUED}
          size={Size.SMALL}
          text={aiButtonText}
          icon={aiButtonIcon}
          onClick={toggleShowPromptBox}
          isLoading={isPromptBoxLoading}
          textWithLoadingState={window.i18next.t('pmwjs_updating')}
          customClasses={showPromptBox ? styles.selectedButtonBackground : styles.unselectedButtonBackground}
        />
      );
    }

    return (
      <div className={styles.premiumButtonContainer}>
        <AiButton
          variant={AIButtonVariant.SUBDUED}
          size={Size.SMALL}
          text={aiButtonText}
          icon={aiButtonIcon}
          onClick={(): void => {
            window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT);
          }}
          isLoading={isPromptBoxLoading}
          textWithLoadingState={window.i18next.t('pmwjs_updating')}
        />
        <Icon icon="icon-crown" type={IconType.PREMIUM} className={`${styles.premiumIcon} color-premium`} size={IconSize.SIZE_ICON_16} />
      </div>
    );
  };

  const getGenerateImagesToggleSwitch = (): ReactElement<any> | null => {
    if (!isImageGenerationEnabled) {
      return null;
    }
    return (
      <div className="flex-row-justify-between flex-align-items-center spacing-p-t-3 spacing-p-r-3 spacing-p-l-3">
        <Text val={'Update Images'} size={TextSize.XXSMALL} bold />
        <ToggleSwitch id="image-generation-toggle" onChange={toggleGenerateImagesUsingAi} isChecked={generateImagesUsingAi} size={ToggleSwitchV2Size.SMALL} className=" -primary" />
      </div>
    );
  };

  const getGenerateDesignsToggleSwitch = (): ReactElement<any> | null => {
    if (!isDesignUpdatesEnabled) {
      return null;
    }

    return (
      <div className="flex-row-justify-between flex-align-items-center spacing-p-t-3 spacing-p-r-3 spacing-p-l-3">
        <Text val={'Update Designs'} size={TextSize.XXSMALL} bold />
        <ToggleSwitch
          id="design-generation-toggle"
          onChange={toggleGenerateDesignsUsingAi}
          isChecked={generateDesignsUsingAi}
          size={ToggleSwitchV2Size.SMALL}
          className=" -primary"
        />
      </div>
    );
  };

  const renderPromptBoxHeader = (): ReactElement<any> => {
    return (
      <div className="_full-width flex-row-justify-between flex-row-align-center">
        {getHeaderLabel()}
        {getHeaderButton()}
      </div>
    );
  };

  const getAiTextPromptBox = (): ReactElement<any> => {
    return (
      <AiTextPromptBox
        {...props}
        rewriteContent={regenerateEmailContent}
        defaultPrompt={getDefaultPrompt()}
        defaultGenerationOptions={getDefaultGenerationOptions()}
        onPromptChange={setDefaultPrompt}
        onGenerationOptionChange={updateDefaultGenerationOption}
        variant={AiPromptBoxVariant.MINIATURE}
      />
    );
  };

  const renderTextEditorInputBox = (): ReactElement<any> => {
    return (
      <div className={emailEditorAiPromptBoxVariant === EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR ? `${styles.inputBoxContainer} spacing-m-t-2` : ''}>
        {getAiTextPromptBox()}
      </div>
    );
  };

  const renderTemplateEditorInputBox = (): ReactElement<any> => {
    return (
      <div className={`${styles.inputBoxContainer} spacing-m-t-2`}>
        {getGenerateImagesToggleSwitch()}
        {getGenerateDesignsToggleSwitch()}
        {getAiTextPromptBox()}
      </div>
    );
  };

  const renderInputBox = (): ReactElement<any> | null => {
    if (!showPromptBox || isPromptBoxLoading) {
      return null;
    }

    switch (emailEditorAiPromptBoxVariant) {
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR_DROPDOWN:
      case EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR:
        return renderTextEditorInputBox();
      case EmailEditorAiPromptBoxVariant.TEMPLATE_EDITOR:
        return renderTemplateEditorInputBox();
    }
  };

  const getAiTextPromptBoxErrorClasses = (): string => {
    return emailEditorAiPromptBoxVariant === EmailEditorAiPromptBoxVariant.SINGLE_TEXT_EDITOR_DROPDOWN
      ? styles.singleComponentErrorBox
      : 'spacing-p-t-5 spacing-p-b-5 spacing-m-t-4 spacing-m-b-4 radius-8';
  };

  const renderErrorBox = (): ReactElement<any> => {
    return <AiTextPromptBoxError isError errorMessage={errorBoxMessage} isHidden={!showErrorBox()} containerClasses={getAiTextPromptBoxErrorClasses()} />;
  };

  const renderPromptBoxWithHeader = (): ReactElement<any> => {
    return (
      <div>
        {renderPromptBoxHeader()}
        {renderErrorBox()}
        {renderInputBox()}
      </div>
    );
  };

  const renderPromptBoxWithoutHeader = (): ReactElement<any> => {
    return (
      <div>
        {renderErrorBox()}
        {renderInputBox()}
      </div>
    );
  };

  const renderPromptBox = (): ReactElement<any> => {
    return addHeader ? renderPromptBoxWithHeader() : renderPromptBoxWithoutHeader();
  };

  return renderPromptBox();
}
